import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card } from "@mui/material";
import { GAME_GRADES, SKILL_BY_PART, SKILL_BY_SCORE, skillLabel } from "../../constants/game-skills";

// Tips for the skills the game can practise.
const TIPS = {
  GM: "Read each highlighted sentence aloud, then rewrite it so it sounds right.",
  SP: "Pick the 3 spelling mistakes you find hardest and write each one out 3 times.",
  VC: "Choose one plain word in your essay and replace it with a more exact one.",
};

// Tips for the parts inside Structure & Flow, Development and Relevance & Logic, keyed by the part's title.
const PART_TIPS = {
  organisation: "Plan your story with a beginning, a middle and an end before you write.",
  transitions: "Start some sentences with time words like 'First', 'Then', 'After that' and 'Finally'.",
  paragraphing: "Start a new paragraph when the time, the place or the part of the story changes.",
  "title relevance": "Check that your title tells the reader what your story is really about.",
  logic: "Check that each event leads to the next one, and say why things happen.",
  intro: "Open with who, what, when and where, using a lively first sentence.",
  body: "Add what you saw, heard and felt at the most exciting moment.",
  conclusion: "End by saying how you felt and what you learned or will do next.",
  relevance: "Read the topic again and cut any part that does not fit it.",
};

const PRACTICE_GRADES = GAME_GRADES;

// The learner's own mistakes, handed to the game so it can turn them into practice questions.
function essayErrorsFrom(scores) {
  const out = [];
  (scores || []).forEach((s) => {
    const info = s.scoreInfo || {};
    if (s.c === "GM") {
      (info.errors || []).forEach((e) => e && e.w && e.r && out.push({ skill: "grammar", w: e.w, r: e.r }));
    }
    if (s.c === "SP") {
      [info.levelItems0, info.levelItems1, info.levelItems2].forEach((list) =>
        (list || []).forEach((e) => e && e.w && e.r && out.push({ skill: "spelling", w: e.w, r: e.r }))
      );
    }
  });
  return out.slice(0, 12);
}

// The lowest-scoring part inside a score such as Structure & Flow (null when it has no parts).
function weakestPart(score) {
  const parts = (score.sub || []).filter((p) => p && p.title && typeof p.score === "number");
  return parts.length > 0 ? parts.reduce((a, b) => (b.score < a.score ? b : a)) : null;
}

export default function NextStepCard({ scores, grade }) {
  const navigate = useNavigate();
  const ranked = (scores || []).filter((s) => typeof s.p === "number" && s.n);
  if (ranked.length === 0) return null;

  const weakest = ranked.reduce((a, b) => (b.p < a.p ? b : a));
  const isGameScore = ["GM", "VC", "SP"].includes(weakest.c);
  const part = isGameScore ? null : weakestPart(weakest);
  const tip = TIPS[weakest.c] || (part && PART_TIPS[part.title.toLowerCase()]) || null;

  // The game always matches the headline: the weakest part if there is one, otherwise the weakest area.
  const gameSkill = (part && SKILL_BY_PART[part.title.toLowerCase()]) || SKILL_BY_SCORE[weakest.c] || null;
  const canPlay = gameSkill && PRACTICE_GRADES.includes(grade);

  return (
    <Card
      id="next-step-card"
      variant="outlined"
      sx={{ padding: 2, marginBottom: 2, borderRadius: "24px", backgroundColor: "#FFF1BF", border: "3px solid #25204A", boxShadow: "0 6px 0 #25204A" }}
    >
      <Box display="flex" alignItems="center" sx={{ fontFamily: "'Playpen Sans', Inter, sans-serif", fontWeight: 800, fontSize: "1.4rem" }}>
        <span aria-hidden="true" style={{ marginRight: 8 }}>🎯</span>
        Your next step
      </Box>
      <Box sx={{ marginTop: 0.5 }}>
        Start with <b>{weakest.n}</b>
        {part ? <>, especially <b>{part.title}</b></> : null}. It is your lowest score, so a little work here helps the most.
      </Box>
      {tip && (
        <Box sx={{ marginTop: 0.5 }}>
          <b>Try this:</b> {tip}
        </Box>
      )}
      {canPlay && (
        <Button
          variant="contained"
          color="success"
          sx={{ marginTop: 1 }}
          onClick={() => navigate(`/game?skill=${gameSkill}&grade=${grade}`, { state: { errors: essayErrorsFrom(scores) } })}
        >
          Practise {skillLabel(gameSkill).toLowerCase()}
        </Button>
      )}
    </Card>
  );
}
